// The Battlemage's standing on the board, host-first.
//
// Bloodshed's pledge (100 Ferocity to keep the Rush of Battle stacks
// through a defeat) is taken at setup, where the Oracle's omen and the
// avatar ceremonies run. The server stages a durable modal pendingChoice
// (promptKind 'battlemage') for each seat with something to guard;
// `foldBattlemagePromptFromMirror` turns it into the overlay for the
// chooser and the seal answers it. The standing itself (stacks, Ferocity,
// the pledge) is REST state on the player row; the prompt only holds the
// start. The Ferocity chip reads the standing for the rest of the match.
//
// Host contract: props (isSpectating, replayDriver, joinRoomCode), state
// (roomCode, battlemageStanding, battlemagePrompt, battlemageBusy,
// battlemageError), setState, _localSeat, _sendMatchIntent.
import { getSkillState } from '../../../utils/arena/skills';
import { hasStanding, fetchStanding, commitPledge } from '../../../utils/arena/battlemage';
import { chooseIntent } from '../../../utils/game/net/automationIntents';
import { playUI, UI } from '../../../utils/arena/uiSounds';

const roomCodeOf = (host) => host.state.roomCode || host.props.joinRoomCode || null;

// `state.battlemageStanding`: undefined until asked, null when this player
// has no standing here (no Ferocity in play, spectating, a room that pays
// nothing), else the server view.
export async function loadStanding(host) {
  if (host.state.battlemageStanding !== undefined || host._battlemageLoading) return;
  if (host.props.isSpectating || host.props.replayDriver || !hasStanding(getSkillState())) {
    host.setState({ battlemageStanding: null });
    return;
  }
  const roomCode = roomCodeOf(host);
  if (!roomCode) return;
  host._battlemageLoading = true;
  try {
    const view = await fetchStanding(roomCode);
    // A view that landed meanwhile (a reconnect fold) wins over this one.
    if (host._unmounted || host.state.battlemageStanding !== undefined) return;
    host.setState({ battlemageStanding: view.unavailable ? null : view });
  } catch (err) {
    console.warn('[battlemage] standing failed:', err?.message || err);
    if (!host._unmounted) host.setState({ battlemageStanding: null });
  } finally {
    host._battlemageLoading = false;
  }
}

// Answer the setup prompt without asking: 'sealed' when the standing already
// carries the pledge (a reconnect), null when this seat has nothing to guard.
export function answerPrompt(host, value) {
  const prompt = host.state.battlemagePrompt;
  if (prompt?.promptId) host._sendMatchIntent?.(chooseIntent(prompt.promptId, value, prompt.seat));
  host.setState({ battlemagePrompt: null });
}

// Seal the pledge on the server, then let the start director move on.
export async function sealPledge(host, guard) {
  const standing = host.state.battlemageStanding;
  if (!standing?.pending || host.state.battlemageBusy) return;
  host.setState({ battlemageBusy: true, battlemageError: null });
  try {
    const view = await commitPledge(standing.pending.roomCode, guard);
    playUI(guard ? UI.CONFIRM : UI.CANCEL, { volume: 0.7 });
    host.setState({ battlemageStanding: view, battlemageBusy: false });
    answerPrompt(host, 'sealed');
  } catch (err) {
    host.setState({ battlemageBusy: false, battlemageError: err?.message || null });
  }
}

// Fold the server's setup prompt. The chooser gets the overlay; the other
// seat just waits. Returns true while the prompt is a battlemage prompt.
export function foldBattlemagePromptFromMirror(host, pc) {
  const mine = !!pc && pc.promptKind === 'battlemage';
  if (!mine) {
    if (host.state.battlemagePrompt) host.setState({ battlemagePrompt: null });
    return false;
  }
  if (!host.props?.isCasualSandbox && pc.seat !== host._localSeat?.()) return true;
  if (host.state.battlemagePrompt?.promptId === pc.promptId) return true;
  host.setState({ battlemagePrompt: { promptId: pc.promptId, seat: pc.seat } });
  loadStanding(host);
  return true;
}
